'use client'

import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'

export default function TermsOfServicePage() {
  const lastUpdated = 'January 6, 2026'
  const companyEmail = 'legal@landlordnoagent.com'

  return (
    <div className="min-h-screen bg-stone-50">
      {/* Header */}
      <header className="bg-white border-b border-stone-200 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center space-x-2 text-stone-600 hover:text-emerald-600 transition-colors">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Home</span>
          </Link>
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.png" alt="LandlordNoAgent Logo" className="w-12 h-12 object-contain" />
            <span className="text-lg font-bold text-stone-800">
              Landlord<span className="text-emerald-600">NoAgent</span>
            </span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-4 py-12">
        <div className="bg-white rounded-2xl shadow-sm border border-stone-100 p-8 md:p-12">
          <h1 className="text-3xl md:text-4xl font-bold text-stone-900 mb-4">Terms of Service</h1>
          <p className="text-stone-500 mb-8">Last Updated: {lastUpdated}</p>

          <div className="prose prose-stone max-w-none">
            {/* Introduction */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">1. Introduction and Acceptance</h2>
              <p className="text-stone-600 mb-4">
                Welcome to LandLordNoAgent ("we," "our," "us," or the "Platform"). These Terms of Service ("Terms") govern your access to and use of our website, mobile applications, and services (collectively, the "Services").
              </p>
              <p className="text-stone-600 mb-4">
                By accessing or using our Services, you agree to be bound by these Terms. If you do not agree to these Terms, you may not access or use the Services.
              </p>
              <p className="text-stone-600">
                We reserve the right to modify these Terms at any time. We will notify you of any changes by posting the new Terms on this page and updating the "Last Updated" date. Your continued use of the Services after any changes constitutes your acceptance of the new Terms.
              </p>
            </section>

            {/* Definitions */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">2. Definitions</h2>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li><strong>"Landlord"</strong> refers to property owners or managers who list properties for rent on the Platform.</li>
                <li><strong>"Tenant" or "Client"</strong> refers to individuals seeking to rent properties through the Platform.</li>
                <li><strong>"User"</strong> refers to any individual who accesses or uses the Services, including Landlords, Tenants, and visitors.</li>
                <li><strong>"Property"</strong> refers to any real estate listing on the Platform.</li>
                <li><strong>"Application"</strong> refers to a rental application submitted by a Tenant for a Property.</li>
                <li><strong>"Escrow"</strong> refers to the temporary holding of funds by the Platform during a transaction.</li>
              </ul>
            </section>

            {/* Eligibility */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">3. Eligibility</h2>
              <p className="text-stone-600 mb-4">
                To use our Services, you must:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li>Be at least 18 years of age</li>
                <li>Have the legal capacity to enter into binding contracts</li>
                <li>Provide accurate, current, and complete information during registration</li>
                <li>Not be prohibited from using the Services under applicable laws</li>
                <li>Complete the Know Your Customer (KYC) verification process where required</li>
              </ul>
            </section>

            {/* User Accounts */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">4. User Accounts</h2>
              <h3 className="text-lg font-medium text-stone-700 mb-2">4.1 Account Registration</h3>
              <p className="text-stone-600 mb-4">
                You must create an account to access certain features of the Services. You agree to provide accurate information and keep your account credentials secure. You are responsible for all activities that occur under your account.
              </p>
              <h3 className="text-lg font-medium text-stone-700 mb-2">4.2 Account Types</h3>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li><strong>Landlord Account:</strong> For property owners to list and manage properties</li>
                <li><strong>Tenant Account:</strong> For individuals seeking rental properties</li>
              </ul>
              <h3 className="text-lg font-medium text-stone-700 mb-2">4.3 KYC Verification</h3>
              <p className="text-stone-600">
                Certain features require identity verification (KYC). You must provide valid identification documents as requested. We reserve the right to reject or suspend accounts that fail verification.
              </p>
            </section>

            {/* Landlord Terms */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">5. Landlord Terms</h2>
              <h3 className="text-lg font-medium text-stone-700 mb-2">5.1 Property Listings</h3>
              <p className="text-stone-600 mb-4">
                Landlords may list properties on the Platform subject to the following conditions:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li>You must have legal authority to rent the property</li>
                <li>All listing information must be accurate and current</li>
                <li>Images and descriptions must accurately represent the property</li>
                <li>You must maintain the property as described</li>
                <li>Listings are subject to verification and approval by the Platform</li>
              </ul>
              <h3 className="text-lg font-medium text-stone-700 mb-2">5.2 Commission and Fees</h3>
              <p className="text-stone-600 mb-4">
                The Platform charges a commission of <strong>10% (ten percent)</strong> on each successful rental transaction. This commission is automatically deducted when escrow funds are released to the landlord. The commission rate may be adjusted with prior notice.
              </p>
              <h3 className="text-lg font-medium text-stone-700 mb-2">5.3 Payment and Escrow</h3>
              <p className="text-stone-600">
                Rental payments are held in escrow for a period of <strong>10 (ten) days</strong> to allow for property inspection and document exchange. After this period, funds (minus the Platform commission) are released to the landlord upon confirmation or automatically if no disputes are raised.
              </p>
            </section>

            {/* Tenant Terms */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">6. Tenant Terms</h2>
              <h3 className="text-lg font-medium text-stone-700 mb-2">6.1 Property Applications</h3>
              <p className="text-stone-600 mb-4">
                Tenants may apply for properties through the Platform. By submitting an application, you:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li>Confirm all information provided is accurate and truthful</li>
                <li>Authorize verification of the information provided</li>
                <li>Understand that applications are subject to landlord approval</li>
              </ul>
              <h3 className="text-lg font-medium text-stone-700 mb-2">6.2 Payments</h3>
              <p className="text-stone-600">
                All payments are processed through our secure payment provider (Stripe). By making a payment, you agree to the payment processor's terms of service. Payments are held in escrow to protect both parties.
              </p>
            </section>

            {/* Payment Terms */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">7. Payment Terms</h2>
              <h3 className="text-lg font-medium text-stone-700 mb-2">7.1 Payment Processing</h3>
              <p className="text-stone-600 mb-4">
                All payments are processed through Stripe, a third-party payment processor. We do not store credit card information on our servers.
              </p>
              <h3 className="text-lg font-medium text-stone-700 mb-2">7.2 Escrow Period</h3>
              <p className="text-stone-600 mb-4">
                Rent payments are held in escrow for 10 days. During this period, tenants should visit the property and confirm receipt of necessary documents. The escrow may be released earlier upon mutual confirmation.
              </p>
              <h3 className="text-lg font-medium text-stone-700 mb-2">7.3 Refunds</h3>
              <p className="text-stone-600">
                Refund requests during the escrow period will be reviewed on a case-by-case basis. Refunds after escrow release require landlord consent or a valid dispute resolution.
              </p>
            </section>

            {/* Prohibited Conduct */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">8. Prohibited Conduct</h2>
              <p className="text-stone-600 mb-4">Users agree not to:</p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li>Provide false or misleading information</li>
                <li>List properties you do not have authority to rent</li>
                <li>Engage in fraudulent activities</li>
                <li>Circumvent Platform fees by arranging direct payments</li>
                <li>Harass, abuse, or harm other users</li>
                <li>Post discriminatory content</li>
                <li>Violate any applicable laws or regulations</li>
                <li>Interfere with the Platform's operations</li>
                <li>Use automated systems to access the Platform without permission</li>
              </ul>
            </section>

            {/* Dispute Resolution */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">9. Dispute Resolution</h2>
              <p className="text-stone-600 mb-4">
                In case of disputes between landlords and tenants:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li>Users should first attempt to resolve disputes directly</li>
                <li>If unresolved, disputes can be escalated to Platform support</li>
                <li>The Platform may mediate disputes but is not obligated to do so</li>
                <li>Escrow funds may be held pending dispute resolution</li>
                <li>The Platform's decision in disputes is final and binding</li>
              </ul>
            </section>

            {/* Limitation of Liability */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">10. Limitation of Liability</h2>
              <p className="text-stone-600 mb-4">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600">
                <li>The Platform is provided "as is" without warranties of any kind</li>
                <li>We do not guarantee the accuracy of property listings</li>
                <li>We are not responsible for the conduct of users</li>
                <li>We are not liable for any indirect, incidental, or consequential damages</li>
                <li>Our total liability shall not exceed the fees paid by you in the preceding 12 months</li>
              </ul>
            </section>

            {/* Indemnification */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">11. Indemnification</h2>
              <p className="text-stone-600">
                You agree to indemnify, defend, and hold harmless LandLordNoAgent, its officers, directors, employees, and agents from any claims, damages, losses, or expenses (including legal fees) arising from your use of the Services, violation of these Terms, or violation of any third-party rights.
              </p>
            </section>

            {/* Termination */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">12. Termination</h2>
              <p className="text-stone-600 mb-4">
                We may suspend or terminate your account at any time for:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-stone-600 mb-4">
                <li>Violation of these Terms</li>
                <li>Fraudulent or illegal activities</li>
                <li>Failure to complete KYC verification</li>
                <li>Extended inactivity</li>
                <li>At our sole discretion with notice</li>
              </ul>
              <p className="text-stone-600">
                Upon termination, pending transactions will be completed or refunded as appropriate. Your obligation to pay any outstanding fees survives termination.
              </p>
            </section>

            {/* Governing Law */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">13. Governing Law</h2>
              <p className="text-stone-600">
                These Terms shall be governed by and construed in accordance with the laws of the Federal Republic of Nigeria. Any disputes shall be resolved in the courts of Lagos, Nigeria.
              </p>
            </section>

            {/* Contact Information */}
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-stone-800 mb-4">14. Contact Information</h2>
              <p className="text-stone-600 mb-4">
                For questions about these Terms, please contact us at:
              </p>
              <div className="bg-stone-50 p-4 rounded-lg">
                <p className="text-stone-700 font-medium">LandLordNoAgent</p>
                <p className="text-stone-600">Email: {companyEmail}</p>
                <p className="text-stone-600">Website: www.landlordnoagent.com</p>
              </div>
            </section>

            {/* Acknowledgment */}
            <section className="mb-4 bg-emerald-50 p-6 rounded-lg border border-emerald-200">
              <h2 className="text-xl font-semibold text-emerald-800 mb-2">Acknowledgment</h2>
              <p className="text-emerald-700">
                By using LandLordNoAgent, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service.
              </p>
            </section>
          </div>
        </div>

        {/* Related Links */}
        <div className="mt-8 flex flex-wrap gap-4 justify-center">
          <Link 
            to="/legal/privacy" 
            className="px-6 py-3 bg-white border border-stone-200 rounded-xl text-stone-700 hover:border-emerald-300 hover:text-emerald-600 transition-colors font-medium"
          >
            Privacy Policy
          </Link>
          <Link 
            to="/help" 
            className="px-6 py-3 bg-white border border-stone-200 rounded-xl text-stone-700 hover:border-emerald-300 hover:text-emerald-600 transition-colors font-medium"
          >
            Help Center
          </Link>
          <Link 
            to="/auth" 
            className="px-6 py-3 bg-emerald-600 text-white rounded-xl hover:bg-emerald-700 transition-colors font-medium"
          >
            Get Started
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="bg-stone-900 text-stone-400 py-8 mt-16">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <p className="text-sm">
            © {new Date().getFullYear()} LandLordNoAgent. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  )
}
